import React from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, Check, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import ShareButton from '../ShareButton';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const ProductCard = ({ product, index }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product);
    toast.success(`${product.name} added to cart!`, {
      description: `₹${product.price.toLocaleString('en-IN')}`,
    });
  };

  const categoryLabels = {
    bracelets: 'Sacred Bracelet',
    ghee: 'Pure Ghee',
    dal: 'Organic Dal',
    rudraksha: 'Rudraksha & Mala',
    pendants: 'Sacred Pendant',
    yantra: 'Sacred Yantra',
    keeda_jadi: 'Himalayan Herb',
    ganga_jal: 'Holy Water',
    idols: 'Sacred Idols',
    other: 'Authentic Product'
  };

  const mrp = product.mrp || Math.round(product.price * 1.35);
  const discount = Math.round(((mrp - product.price) / mrp) * 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: (index % 8) * 0.04 }}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-[0_4px_20px_rgba(78,18,37,0.06)] border border-[#E7D9BC]/70 hover:border-[#D9711F]/60 transition-all duration-300 flex flex-col justify-between"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/store/${product.slug || product.id}`} className="block relative flex-1 flex flex-col justify-between">
        {/* Image Container */}
        <div className="relative h-52 sm:h-52 md:h-56 overflow-hidden bg-[#F3E7D0]/40">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
          />

          {/* Bestseller / Energised Ribbon */}
          {product.bestseller || product.category === 'bracelets' || product.category === 'rudraksha' ? (
            <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 bg-gradient-to-r from-[#6E1B34] to-[#8F2C4A] text-white text-[10px] sm:text-[11px] font-bold tracking-wider uppercase px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-md flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-[#F2A65A]" />
              <span>{product.bestseller ? 'Bestseller' : 'Energised'}</span>
            </div>
          ) : (
            <div className="absolute top-2.5 left-2.5 sm:top-3 sm:left-3 bg-[#4E1225]/85 backdrop-blur-md text-[#FBF4E7] text-[10px] sm:text-[11px] font-semibold tracking-wider uppercase px-2.5 py-0.5 sm:px-3 sm:py-1 rounded-full shadow-sm truncate max-w-[120px]">
              {product.origin || 'Uttarakhand'}
            </div>
          )}

          {/* Discount Badge */}
          {discount > 0 && (
            <div className="absolute bottom-2.5 left-2.5 sm:bottom-3 sm:left-3 bg-[#2E7D45] text-white text-[10px] sm:text-xs font-extrabold px-2 py-0.5 rounded-md shadow-sm">
              {discount}% OFF
            </div>
          )}

          {/* Category Tag & Share */}
          <div className="absolute top-2.5 right-2.5 sm:top-3 sm:right-3 flex items-center space-x-1.5" onClick={(e) => e.stopPropagation()}>
            <ShareButton
              url={`${window.location.origin}/store/${product.slug || product.id}`}
              title={product.name}
              text={`Check out ${product.name} on Paramparik Pooja!`}
            />
          </div>
        </div>

        {/* Product Details */}
        <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between text-xs text-[#83725B] mb-1.5 font-medium">
              <span className="truncate max-w-[130px] sm:max-w-none">{categoryLabels[product.category] || 'Sacred Item'}</span>
              <div className="flex items-center gap-1 text-[#D9711F]">
                <Star className="w-3.5 h-3.5 fill-current" />
                <span className="font-bold text-[#2A1F16]">{product.rating || '4.8'}</span>
              </div>
            </div>

            <h3 className="font-heading text-sm sm:text-base font-bold text-[#4E1225] group-hover:text-[#D9711F] transition-colors line-clamp-1 mb-1.5">
              {product.name}
            </h3>

            <p className="text-[#83725B] text-xs leading-relaxed line-clamp-2 mb-3">
              {product.description}
            </p>
          </div>

          <div>
            <div className="flex items-center space-x-1.5 text-[11px] sm:text-xs text-[#2E7D45] font-semibold mb-3">
              <Check className="w-3.5 h-3.5 flex-shrink-0" />
              <span>100% Authentic &amp; Blessed</span>
            </div>

            {/* Price & Action Row */}
            <div className="flex items-center justify-between pt-3 border-t border-[#E7D9BC]/50 gap-2">
              <div className="flex items-baseline gap-1.5 leading-tight">
                <span className="text-lg sm:text-xl font-bold font-price text-[#4E1225]">
                  ₹{product.price.toLocaleString('en-IN')}
                </span>
                {mrp > product.price && (
                  <span className="text-xs text-[#83725B] line-through font-normal font-price">
                    ₹{mrp.toLocaleString('en-IN')}
                  </span>
                )}
              </div>

              <Button
                onClick={handleAddToCart}
                className="bg-[#6E1B34] hover:bg-[#4E1225] text-white rounded-xl px-3.5 py-2 text-xs font-semibold shadow-xs flex items-center gap-1.5 hover:scale-105 active:scale-95 transition-all flex-shrink-0"
                data-testid={`add-to-cart-${product.id}`}
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                <span>Add</span>
              </Button>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
